import { faker } from "@faker-js/faker";

import {
  fakeAppointmentTypeGroup,
  fakeBaseUserAvatarProps,
  fakeBaseUserType,
  fakeEventRecurrence,
  fakeIllustrationName,
  fakePronouns,
  fakeProviderType,
} from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";
import { GroupClassCarouselVariantValues } from "@/constants/Schedule";
import type { ScheduleEventCardProps } from "@/ui/components/schedule/ScheduleEventCard";

const fakeAppointmentStatus = (): AppointmentStatus =>
  faker.helpers.arrayElement([
    "cancelled",
    "completed",
    "no show",
    "scheduled",
  ]);

const fakeAppointmentRecurrence = (): AppointmentRecurrenceResponse =>
  faker.helpers.arrayElement([
    "biweekly",
    "Biweekly",
    "does not repeat",
    "Does not repeat",
    "weekly",
    "Weekly",
  ]);

const fakeProviderAttendee = (
  overrides?: DeepPartial<ProviderAttendee>,
): ProviderAttendee => {
  const attendee: ProviderAttendee = {
    externalId: faker.string.uuid(),
    name: faker.person.fullName(),
    pronouns: fakePronouns({}),
    providerType: fakeProviderType(),
  };
  return merge(attendee, overrides);
};

const fakeSupportAttendee = (
  overrides?: DeepPartial<SupportAttendee>,
): SupportAttendee => {
  const attendee: SupportAttendee = {
    chosenName: faker.person.firstName(),
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    pronouns: fakePronouns({}),
    relationToPatient: faker.helpers.arrayElement(["Self", "Parent", "Spouse"]),
  };
  return merge(attendee, overrides);
};

const fakeAppointmentPatient = (
  overrides?: DeepPartial<AppointmentPatient>,
): AppointmentPatient => {
  const patient: AppointmentPatient = {
    chosenName: faker.person.firstName(),
    dateOfBirth: faker.date.birthdate().toISOString(),
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    pronouns: fakePronouns({}),
  };
  return merge(patient, overrides);
};

const fakeScheduleEventCardProps = (
  overrides?: DeepPartial<ScheduleEventCardProps>,
): ScheduleEventCardProps => {
  const props: ScheduleEventCardProps = {
    attendees: faker.helpers.multiple(() => fakeBaseUserAvatarProps(), {
      count: 2,
    }),
    description: faker.lorem.sentence(),
    endDate: faker.date.soon().toISOString(),
    externalId: faker.string.uuid(),
    frequency: fakeEventRecurrence(),
    iconName: fakeIllustrationName(),
    isGroupClassEvent: true,
    isRecurring: true,
    selectedUserId: faker.string.uuid(),
    startDate: faker.date.recent().toISOString(),
    title: faker.word.words({ count: { max: 5, min: 2 } }),
  };
  return merge(props, overrides);
};

const fakeAppointment = (
  overrides?: DeepPartial<AppointmentDetailResponse>,
): AppointmentDetailResponse => {
  // eslint-disable-next-line no-restricted-syntax -- appointments as defined by the backend API have >15 properties
  const appointment: AppointmentDetailResponse = {
    appointmentEndDateTime: faker.date.soon().toISOString(),
    appointmentExternalId: faker.string.uuid(),
    appointmentStartDateTime: faker.date.recent().toISOString(),
    appointmentStatus: fakeAppointmentStatus(),
    appointmentType: faker.word.words({ count: { max: 3, min: 1 } }),
    appointmentTypeLabel: faker.word.words({ count: { max: 3, min: 1 } }),
    calendarProfile: {
      calendarId: faker.string.uuid(),
      sub: faker.string.uuid(),
      userExternalId: faker.string.uuid(),
    },
    createdAt: faker.date.past().toISOString(),
    createdBy: faker.string.uuid(),
    description: faker.lorem.sentence(),
    eventId: faker.string.uuid(),
    host: fakeProviderAttendee(),
    isPatientAttending: faker.datatype.boolean(),
    patient: fakeAppointmentPatient(),
    providerAttendees: faker.helpers.multiple(() => fakeProviderAttendee(), {
      count: faker.number.int({ max: 3, min: 1 }),
    }),
    recurrence: fakeAppointmentRecurrence(),
    supportAttendees: faker.helpers.multiple(() => fakeSupportAttendee(), {
      count: faker.number.int({ max: 3, min: 1 }),
    }),
    typeGroup: fakeAppointmentTypeGroup(),
    updatedAt: faker.date.recent().toISOString(),
    updatedBy: faker.string.uuid(),
  };
  return merge(appointment, overrides);
};

const fakeGroupClassEventHost = (
  overrides?: DeepPartial<GroupClassEventHost>,
): GroupClassEventHost => {
  const host: GroupClassEventHost = {
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    pronouns: fakePronouns({}),
    providerType: fakeProviderType(),
    type: fakeBaseUserType(),
  };
  return merge(host, overrides);
};

const fakeGroupClassEvent = (
  overrides?: DeepPartial<GroupClassEventResponse>,
): GroupClassEventResponse => {
  const event: GroupClassEventResponse = {
    description: faker.word.words({ count: { max: 10, min: 2 } }),
    endTime: faker.date.soon().toISOString(),
    frequency: fakeEventRecurrence(),
    groupClassEventExternalId: faker.string.uuid(),
    groupClassExternalId: faker.string.uuid(),
    hosts: faker.helpers.multiple(() => fakeGroupClassEventHost(), {
      count: faker.number.int({ max: 3, min: 1 }),
    }),
    iconName: fakeIllustrationName(),
    isSensitive: faker.datatype.boolean(),
    startTime: faker.date.recent().toISOString(),
    title: faker.word.words({ count: { max: 5, min: 2 } }),
  };
  return merge(event, overrides);
};

const fakeGroupClassCarouselVariant = () =>
  faker.helpers.arrayElement(Object.values(GroupClassCarouselVariantValues));

export {
  fakeAppointment,
  fakeAppointmentPatient,
  fakeGroupClassCarouselVariant,
  fakeGroupClassEvent,
  fakeGroupClassEventHost,
  fakeProviderAttendee,
  fakeScheduleEventCardProps,
  fakeSupportAttendee,
};
